import { useState, useEffect } from 'react';
import { Music2, Play, Pause, Shuffle, Search, ChevronDown, Disc3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { usePlayer } from '../context/PlayerContext';

const BACKEND_URL = "";

const Music = () => {
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedArtist, setSelectedArtist] = useState('all');
  const [viewMode, setViewMode] = useState('tracks'); // 'tracks' or 'artists'
  const { currentTrack, isPlaying, playTrack, shufflePlaylist, refreshPlaylist } = usePlayer();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tracksRes, artistsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tracks?limit=100`),
        fetch(`${BACKEND_URL}/api/artists`)
      ]);
      
      if (tracksRes.ok) {
        const tracksData = await tracksRes.json();
        setTracks(tracksData);
      }
      
      if (artistsRes.ok) {
        const artistsData = await artistsRes.json();
        setArtists(artistsData);
      }
    } catch (error) {
      console.error('Error fetching music data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique genres from tracks
  const genres = [...new Set(tracks.map(t => t.genre).filter(Boolean))].sort();

  // Filter tracks based on search and filters
  const filteredTracks = tracks.filter(track => {
    const matchesSearch = searchTerm === '' || 
      track.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      track.artist_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGenre = selectedGenre === 'all' || track.genre === selectedGenre;
    const matchesArtist = selectedArtist === 'all' || track.artist_name === selectedArtist;
    
    return matchesSearch && matchesGenre && matchesArtist;
  });

  // Group tracks by artist
  const tracksByArtist = filteredTracks.reduce((acc, track) => {
    if (!acc[track.artist_name]) {
      acc[track.artist_name] = [];
    }
    acc[track.artist_name].push(track);
    return acc;
  }, {});

  // Sort artists by number of tracks
  const sortedArtists = Object.entries(tracksByArtist)
    .sort((a, b) => b[1].length - a[1].length);

  const handlePlayAll = () => {
    if (filteredTracks.length > 0) {
      playTrack(filteredTracks[0]);
    }
  };

  const handleShuffleAll = () => {
    if (filteredTracks.length > 0) {
      shufflePlaylist();
      playTrack(filteredTracks[Math.floor(Math.random() * filteredTracks.length)]);
    }
  };

  const handlePlayArtist = (artistTracks) => {
    playTrack(artistTracks[0]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Disc3 className="w-16 h-16 text-purple-500 animate-spin" />
          <p className="text-gray-400">Loading music catalog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 pb-32">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-blue-600/20 to-purple-600/30" />
        <div className="relative px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <Music2 className="w-10 h-10 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">Music Catalog</h1>
                <p className="text-gray-400 mt-1">Discover local Twin Cities artists</p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex gap-6 mt-6">
              <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-2">
                <p className="text-2xl font-bold text-purple-400">{tracks.length}</p>
                <p className="text-sm text-gray-400">Tracks</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-2">
                <p className="text-2xl font-bold text-blue-400">{artists.length}</p>
                <p className="text-sm text-gray-400">Artists</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-2">
                <p className="text-2xl font-bold text-pink-400">{genres.length}</p>
                <p className="text-sm text-gray-400">Genres</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search songs or artists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>
            
            {/* Genre Filter */}
            <div className="relative">
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="appearance-none bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all" className="bg-gray-900">All Genres</option>
                {genres.map(genre => (
                  <option key={genre} value={genre} className="bg-gray-900">{genre}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Artist Filter */}
            <div className="relative">
              <select
                value={selectedArtist}
                onChange={(e) => setSelectedArtist(e.target.value)}
                className="appearance-none bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all" className="bg-gray-900">All Artists</option>
                {[...new Set(tracks.map(t => t.artist_name))].sort().map(artist => (
                  <option key={artist} value={artist} className="bg-gray-900">{artist}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button
              onClick={handlePlayAll}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Play All ({filteredTracks.length})
            </Button>
            <Button
              onClick={handleShuffleAll}
              variant="outline"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
            >
              <Shuffle className="w-4 h-4 mr-2" />
              Shuffle
            </Button>
            
            {/* View Toggle */}
            <div className="flex gap-1 ml-auto bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setViewMode('tracks')}
                className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === 'tracks' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                All Tracks
              </button>
              <button
                onClick={() => setViewMode('artists')}
                className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === 'artists' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                By Artist
              </button>
            </div>
          </div>

          {/* Track List View */}
          {viewMode === 'tracks' && (
            <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 text-sm text-gray-400 font-medium">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Title</div>
                <div className="col-span-3">Artist</div>
                <div className="col-span-3">Genre</div>
              </div>
              
              {/* Tracks */}
              <div className="divide-y divide-white/5">
                {filteredTracks.map((track, index) => (
                  <div
                    key={track.id}
                    onClick={() => playTrack(track)}
                    className={`grid grid-cols-12 gap-4 px-4 py-3 hover:bg-white/5 cursor-pointer transition-colors group ${
                      currentTrack?.id === track.id ? 'bg-purple-500/20' : ''
                    }`}
                  >
                    <div className="col-span-1 flex items-center">
                      {currentTrack?.id === track.id && isPlaying ? (
                        <div className="flex items-center gap-0.5">
                          <span className="w-1 h-4 bg-purple-500 animate-pulse rounded-full" />
                          <span className="w-1 h-3 bg-purple-500 animate-pulse rounded-full" style={{ animationDelay: '0.2s' }} />
                          <span className="w-1 h-5 bg-purple-500 animate-pulse rounded-full" style={{ animationDelay: '0.4s' }} />
                        </div>
                      ) : (
                        <span className="text-gray-500 group-hover:hidden">{index + 1}</span>
                      )}
                      <Play className="w-4 h-4 text-white hidden group-hover:block" />
                    </div>
                    <div className="col-span-5 flex items-center gap-3">
                      <img
                        src={track.thumbnail}
                        alt={track.title}
                        className="w-10 h-10 rounded object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100';
                        }}
                      />
                      <span className={`font-medium truncate ${
                        currentTrack?.id === track.id ? 'text-purple-400' : 'text-white'
                      }`}>
                        {track.title}
                      </span>
                    </div>
                    <div className="col-span-3 flex items-center text-gray-400 truncate">
                      {track.artist_name}
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-gray-300">
                        {track.genre}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Artist View */}
          {viewMode === 'artists' && (
            <div className="space-y-6">
              {sortedArtists.map(([artistName, artistTracks]) => {
                const artist = artists.find(a => a.name === artistName);
                return (
                  <div key={artistName} className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
                    {/* Artist Header */}
                    <div className="flex items-center gap-4 p-4 border-b border-white/10">
                      <img
                        src={artist?.image_url || artistTracks[0]?.thumbnail}
                        alt={artistName}
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100';
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white">{artistName}</h3>
                        <p className="text-sm text-gray-400">
                          {artistTracks.length} {artistTracks.length === 1 ? 'track' : 'tracks'}
                        </p>
                      </div>
                      <Button
                        onClick={() => handlePlayArtist(artistTracks)}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Play
                      </Button>
                    </div>
                    
                    {/* Artist Tracks */}
                    <div className="divide-y divide-white/5">
                      {artistTracks.map((track, index) => (
                        <div
                          key={track.id}
                          onClick={() => playTrack(track)}
                          className={`flex items-center gap-4 px-4 py-3 hover:bg-white/5 cursor-pointer transition-colors ${
                            currentTrack?.id === track.id ? 'bg-purple-500/20' : ''
                          }`}
                        >
                          <span className="w-6 text-center text-gray-500">{index + 1}</span>
                          <span className={`flex-1 ${
                            currentTrack?.id === track.id ? 'text-purple-400' : 'text-white'
                          }`}>
                            {track.title}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-gray-400">
                            {track.genre}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredTracks.length === 0 && (
            <div className="text-center py-16">
              <Music2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-400 mb-2">No tracks found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Music;
